import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface BrevoList {
  id: number;
  name: string;
  subscriberCount: number;
  listType: string;
}

export interface BrevoSubscriber {
  email: string;
  firstName: string;
  lastName: string;
}

export interface BrevoFolder {
  id: number;
  name: string;
}

function useInvalidateBrevoLists() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['brevo-lists'] });
    queryClient.invalidateQueries({ queryKey: ['brevo-site-lists'] });
  };
}

// Defaults to the lists associated with this deployment's site (campaign target
// pickers must not offer the other brand's lists). List-management screens pass
// `{ all: true }` to see every list in the shared Brevo account.
export function useBrevoLists(options?: { all?: boolean }) {
  const all = options?.all ?? false;
  return useQuery({
    queryKey: ['brevo-lists', all ? 'all' : 'site'],
    queryFn: () => apiClient<BrevoList[]>(all ? '/brevo/lists?all=true' : '/brevo/lists'),
  });
}

// Brevo requires a folderId to create a list - the old admin's Create List form
// never collected one (its API silently 400'd), so this asks for it explicitly.
export function useBrevoFolders() {
  return useQuery({
    queryKey: ['brevo-folders'],
    queryFn: () => apiClient<BrevoFolder[]>('/brevo/lists/folders'),
  });
}

export function useCreateBrevoList() {
  const invalidate = useInvalidateBrevoLists();
  return useMutation({
    mutationFn: ({ name, folderId }: { name: string; folderId: number }) =>
      apiClient<BrevoList>('/brevo/lists', { method: 'POST', body: JSON.stringify({ name, folderId }) }),
    onSuccess: invalidate,
  });
}

export function useUpdateBrevoList() {
  const invalidate = useInvalidateBrevoLists();
  return useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      apiClient<BrevoList>(`/brevo/lists/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
    onSuccess: invalidate,
  });
}

export function useDeleteBrevoList() {
  const invalidate = useInvalidateBrevoLists();
  return useMutation({
    mutationFn: (id: number) => apiClient<{ success: boolean }>(`/brevo/lists/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useBrevoListSubscribers(listId: number | null, limit = 50, offset = 0) {
  return useQuery({
    queryKey: ['brevo-list-subscribers', listId, limit, offset],
    queryFn: () =>
      apiClient<{ subscribers: BrevoSubscriber[]; total: number }>(
        `/brevo/lists/${listId}/subscribers?limit=${limit}&offset=${offset}`,
      ),
    enabled: listId !== null,
  });
}

export function useAddBrevoSubscriber(listId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; firstName?: string; lastName?: string }) =>
      apiClient<BrevoSubscriber>(`/brevo/lists/${listId}/subscribers`, { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['brevo-list-subscribers', listId] }),
  });
}
